/**
 * AddChildPage Component
 * Search and add children to family class
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, UserPlus, Loader2, ArrowLeft, Users } from 'lucide-react';
import { familyApi, SearchedUser } from '../../services/familyApi';
import { useBasePath } from '../../contexts/BasePathContext';
import { buildStaticUrl } from '../../config/env';

export const AddChildPage: React.FC = () => {
  const navigate = useNavigate();
  const { basePath } = useBasePath();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchedUser[]>([]);
  const [suggestedUsers, setSuggestedUsers] = useState<SearchedUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [loadingSuggested, setLoadingSuggested] = useState(true);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadSuggestedUsers();
  }, []);

  const loadSuggestedUsers = async () => {
    try {
      setLoadingSuggested(true);
      const users = await familyApi.getSuggestedUsers();
      setSuggestedUsers(users);
    } catch (err: any) {
      // Silently fail for suggested users
    } finally {
      setLoadingSuggested(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      setSearching(true);
      setError('');
      setSuccess('');
      const results = await familyApi.searchUser(searchQuery.trim());
      setSearchResults(results);
      if (results.length === 0) {
        setError('未找到匹配的用户');
      }
    } catch (err: any) {
      setError(err.message || '搜索失败');
    } finally {
      setSearching(false);
    }
  };

  const handleAdd = async (user: SearchedUser) => {
    try {
      setAddingId(user.id);
      setError('');
      setSuccess('');
      await familyApi.addChild(user.id);
      setSuccess(`已成功添加「${user.nickname}」为家庭成员`);
      // Remove from search results and suggested
      setSearchResults(prev => prev.filter(u => u.id !== user.id));
      setSuggestedUsers(prev => prev.filter(u => u.id !== user.id));
    } catch (err: any) {
      setError(err.message || '添加失败');
    } finally {
      setAddingId(null);
    }
  };

  const renderUserCard = (user: SearchedUser) => (
    <div key={user.id} className="flex items-center justify-between p-4">
      <div className="flex items-center space-x-3">
        {user.avatar_url ? (
          <img src={buildStaticUrl(user.avatar_url)} alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
            {user.nickname?.charAt(0)?.toUpperCase() || 'U'}
          </div>
        )}
        <div>
          <p className="text-gray-900 dark:text-white font-medium">{user.nickname}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {user.phone || user.email || ''}
          </p>
        </div>
      </div>
      <button
        onClick={() => handleAdd(user)}
        disabled={addingId === user.id}
        className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm disabled:opacity-50"
      >
        {addingId === user.id ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            <UserPlus className="w-4 h-4 mr-1" />
            添加
          </>
        )}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-8">
          <button
            onClick={() => navigate(`${basePath}/family`)}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">添加家庭成员</h1>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-green-600 dark:text-green-400">{success}</p>
          </div>
        )}

        {/* Search */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">搜索用户</h2>
          <div className="flex space-x-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="输入手机号或邮箱搜索"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={searching || !searchQuery.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : '搜索'}
            </button>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-4 divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
              {searchResults.map(renderUserCard)}
            </div>
          )}
        </div>

        {/* Suggested Users */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 p-6 border-b border-gray-200 dark:border-gray-700">
            <Users className="w-5 h-5 text-gray-500" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">推荐用户</h2>
            <span className="text-xs text-gray-400">（通过您的推荐码注册）</span>
          </div>

          {loadingSuggested ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : suggestedUsers.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              暂无通过您的推荐码注册的用户
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {suggestedUsers.map(renderUserCard)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
